import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { IDisplaySlide } from '@pcp/core/interfaces';
import { DrawingToolName } from 'shared/classroom/toolbar/core';
import { FabricSlideCanvasComponent } from './fabric-slide-canvas.component';

/** Layer index in the slide-preview component. 1 = primary, 2 = secondary, 3 = tertiary. */
type Layer = 1 | 2 | 3;

/**
 * PerceptionSlideComponent — the `PerceptionReveal` capability over a fabric slide canvas
 * (canvas migration, `canvas-image-slides`; Decision #10: `FixedImageBackground` +
 * `PerceptionReveal`). It composes {@link FabricSlideCanvasComponent} (which owns the
 * background + drawing + sync) and adds the perception "hide → reveal" presentation that
 * the SVG renderer (`svg-canvas.component`) carried for `data-slide-type='W'`.
 *
 * Behavior reproduced faithfully from the SVG source (verified identical to the
 * pre-migration `develop` baseline):
 *
 * - **Fade is presentation-screen-only.** On a presentation screen (beamer/student), an
 *   unrevealed perception image plays a SINGLE ~5s fade cycle over a black background —
 *   the image AND its drawings fade in, hold, then fade back out, ending hidden (the
 *   `fade-in-fade-out` keyframe, no `infinite`). On the teacher (non-presentation) view the
 *   slide is shown normally (no fade) so the teacher can see it and decide when to reveal.
 *   (NB: the design prose said "repeating" — the actual SVG runs the cycle once; we match
 *   the SVG.)
 * - **Reveal control** (the eye icon) shows ONLY on non-presentation views and only while
 *   unrevealed; it is never shown on a presentation screen. Clicking it emits `reveal`.
 * - **Revealed** pins the image + drawings permanently visible and stops the animation.
 *   The revealed state is the synced Y.Doc display-state (`revealed` input) — source of
 *   truth, restored on late-join / refresh — not a transient local flag.
 *
 * The fade itself stays a LOCAL presentation effect (Decision D2): only the revealed
 * boolean is synced. The whole rendered layer's opacity is animated (Decision D3) so the
 * image and drawings fade together — here by animating the canvas element's opacity over a
 * black host, mirroring the SVG's `.animate:not(.animation-stopped) #drawings, image` fade scoping.
 */
@Component({
    selector: 'pcp-perception-slide',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FabricSlideCanvasComponent, MatIconModule, TranslocoPipe],
    template: `
        <div
            class="perception-host"
            [class.presentation]="isPresentationScreen()"
            [class.revealed]="revealed()"
            [class.animate]="animate()"
        >
            <pcp-fabric-slide-canvas
                [slide]="slide()"
                [layer]="layer()"
                [drawingTool]="drawingTool()"
            ></pcp-fabric-slide-canvas>

            @if (showRevealControl()) {
                <div class="perception-button-container">
                    <button
                        type="button"
                        class="perception-button-wrapper"
                        (click)="reveal.emit()"
                        [attr.aria-label]="'preview.perception.reveal' | transloco"
                        [title]="'preview.perception.reveal' | transloco"
                    >
                        <mat-icon class="perception-button">visibility</mat-icon>
                    </button>
                </div>
            }
        </div>
    `,
    styleUrl: './perception-slide.component.scss',
})
export class PerceptionSlideComponent {
    readonly slide = input.required<IDisplaySlide>();
    readonly layer = input.required<Layer>();
    readonly drawingTool = input<DrawingToolName | null>(null);

    /** Presentation screen (beamer/student): fade applies and the reveal control is hidden. */
    readonly isPresentationScreen = input<boolean>(false);
    /** Synced revealed display-state for this slide (source of truth). */
    readonly revealed = input<boolean>(false);
    /**
     * Synced perception "show epoch". Bumps on a same-slide content reset so the hide animation
     * replays even when the slide was not yet revealed (slide identity is unchanged on a reset, so
     * the `slide()` re-arm trigger alone would miss it).
     */
    readonly showEpoch = input<number>(0);

    /** User activated the reveal control (teacher only). */
    readonly reveal = output<void>();

    /**
     * The nested slide canvas, exposed for the favorites export (`canvas-slide-favorites` D7):
     * `@ViewChild(FabricSlideCanvasComponent)` in the preview element does not descend into
     * this component's view, so the export handle is resolved through this accessor instead.
     */
    readonly fabricCanvas = viewChild(FabricSlideCanvasComponent);

    /** Drives the single-shot fade keyframe; re-armed on each slide change (mirrors SVG `startAnimation`). */
    protected readonly animate = signal(false);

    /** Reveal control: non-presentation views, only while unrevealed (matches the SVG condition). */
    protected readonly showRevealControl = computed(
        () => !this.isPresentationScreen() && !this.revealed(),
    );

    constructor() {
        // Re-arm the fade whenever the shown slide changes (navigate away & back) OR the show
        // epoch bumps (same-slide content reset) so the keyframe restarts. The component instance
        // is reused across slide changes, so the CSS animation would not otherwise replay;
        // toggling the `animate` class off then on forces the browser to restart the keyframe —
        // the SVG renderer does the same (`startAnimation`). Reveal does NOT re-arm (the
        // `.revealed` style pins it visible regardless of `.animate`).
        let timer: ReturnType<typeof setTimeout> | undefined;
        effect((onCleanup) => {
            this.slide(); // dependency: restart on slide change
            this.showEpoch(); // dependency: restart on same-slide content reset
            this.animate.set(false);
            timer = setTimeout(() => this.animate.set(true), 50);
            onCleanup(() => {
                if (timer) clearTimeout(timer);
            });
        });
    }
}
